import React, { useState, useEffect } from 'react';
import { TextField, Select } from '@anyemp/ui-kit';
import ModalPortal from '../UI/ModalPortal';
import ModalFrame from '../UI/ModalFrame';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { X, Eye, EyeOff, Loader } from 'lucide-react';
import { accounts, libraries } from '../../services/apiService';
import toast from 'react-hot-toast';

const EditMyAccountsModal = ({ isOpen, onClose, account }) => {
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);

  // Fetch tools from libraries
  const { data: toolsData } = useQuery({
    queryKey: ['tools'],
    queryFn: libraries.getTools,
    enabled: isOpen
  });

  const tools = toolsData?.data || [];

  // Find default tool (Gmail)
  const gmailTool = tools.find(t => t.name?.toLowerCase() === 'gmail');

  // Form state
  const [accountData, setAccountData] = useState({
    name: '',
    tool_id: '',
    login: '',
    link: '',
    password: '',
    notes: ''
  });

  // Pre-fill form when account changes
  useEffect(() => {
    if (isOpen && account) {
      setShowPassword(false);
      setAccountData({
        name: account.name || '',
        tool_id: gmailTool?.id || account.tool_id || '',
        login: account.login || '',
        link: account.link || '',
        password: account.password || '',
        notes: account.notes || ''
      });
    }
  }, [isOpen, account, gmailTool?.id]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: () => accounts.updateAccountOnly(account.id, accountData),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Account updated successfully!');
        queryClient.invalidateQueries(['myAccounts']);
        onClose();
      } else {
        toast.error(response.error || 'Failed to update account');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update account');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!accountData.name && !accountData.login) {
      toast.error('Name or Login is required');
      return;
    }

    updateMutation.mutate();
  };

  if (!isOpen) return null;

  return (
    <ModalPortal onClose={onClose} ariaLabel="Edit account">
      <ModalFrame size="md" className="-m-3 w-[calc(100%+1.5rem)] rounded-none dark:bg-dark-bg-primary sm:m-0 sm:w-full sm:rounded-lg">
        <form onSubmit={handleSubmit} className="contents">
        {/* Header */}
        <ModalFrame.Header className="items-center">
          <h2 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary">
            Edit Account
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-secondary hover:text-light-text-primary dark:hover:bg-dark-bg-secondary dark:hover:text-dark-text-primary"
          >
            <X className="h-5 w-5" />
          </button>
        </ModalFrame.Header>

        {/* Form */}
        <ModalFrame.Body className="space-y-4">
          <div>
            <div className="flex items-end gap-2">
              <Select
                label="Tool (Auto-selected: Gmail)"
                required
                fullWidth
                disabled
                placeholder="Select a tool..."
                value={accountData.tool_id ? String(accountData.tool_id) : ''}
                onChange={(v) => setAccountData({ ...accountData, tool_id: v || '' })}
                options={tools.map((tool) => ({ value: String(tool.id), label: tool.name }))}
              />
            </div>
            {gmailTool && (
              <span className="text-xs text-green-600 dark:text-green-400 whitespace-nowrap">✓ Found</span>
            )}
            {!gmailTool && (
              <span className="text-xs text-yellow-600 dark:text-yellow-400 whitespace-nowrap">⚠ Not found</span>
            )}
          </div>

          <div>
            <TextField
              label="Name"
              fullWidth
              type="text"
              value={accountData.name}
              onChange={(e) => setAccountData({ ...accountData, name: e.target.value })}
            />
          </div>

          <div>
            <TextField
              label="Login"
              fullWidth
              type="text"
              value={accountData.login}
              onChange={(e) => setAccountData({ ...accountData, login: e.target.value })}
            />
          </div>

          <div>
            <TextField
              label="Link"
              fullWidth
              type="url"
              value={accountData.link}
              onChange={(e) => setAccountData({ ...accountData, link: e.target.value })}
            />
          </div>

          <div>
            <TextField
              label="Password"
              fullWidth
              type={showPassword ? 'text' : 'password'}
              value={accountData.password}
              onChange={(e) => setAccountData({ ...accountData, password: e.target.value })}
              endAdornment={(
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-md text-light-text-secondary hover:bg-light-bg-secondary dark:text-dark-text-secondary dark:hover:bg-dark-bg-secondary"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              )}
            />
          </div>

          <div>
            <TextField
              label="Notes"
              multiline
              rows={3}
              fullWidth
              value={accountData.notes}
              onChange={(e) => setAccountData({ ...accountData, notes: e.target.value })}
            />
          </div>

        </ModalFrame.Body>
        <ModalFrame.Footer className="flex flex-col-reverse gap-3 sm:flex-row sm:flex-wrap sm:justify-end [&>button]:w-full sm:[&>button]:w-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-light-border-primary dark:border-dark-border-primary rounded-lg text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white rounded-lg transition-colors"
            >
              {updateMutation.isPending && <Loader className="h-4 w-4 animate-spin" />}
              <span>{updateMutation.isPending ? 'Updating...' : 'Update'}</span>
            </button>
        </ModalFrame.Footer>
        </form>
      </ModalFrame>
    </ModalPortal>
  );
};

export default EditMyAccountsModal;
